import Link from "next/link";
import { MegaMenuConfig } from "../types";
import { trackMegaMenuLinkClick } from "@/utils/analytics";

interface ClinicModelsPanelProps {
  menu: MegaMenuConfig;
}

export default function ClinicModelsPanel({ menu }: ClinicModelsPanelProps) {
  return (
    <div className="@container relative bg-white border-t border-gray-100 rounded-xl shadow-lg w-full">
      <div className="flex flex-col pt-4 lg:pt-6 @[768px]:pt-6 pb-6 lg:pb-10 @[768px]:pb-10 gap-5 lg:gap-6 @[768px]:gap-6 px-4 lg:px-6 @[768px]:px-6">

        <Link
          href="/models/membership/"
          className="group block rounded-2xl bg-gradient-to-r from-brand-600 to-brand-500 px-5 py-4 lg:px-6 lg:py-5 @[768px]:px-6 @[768px]:py-5 transition-shadow hover:shadow-md"
          onClick={() => {
            trackMegaMenuLinkClick({
              menu: menu.id,
              linkText: "Model Club Unlimited",
              linkUrl: "/models/membership/",
            });
          }}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between @[480px]:flex-row @[480px]:items-center @[480px]:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-white/70">
                Model Club Unlimited
              </p>
              <p className="mt-1 text-base font-bold text-white lg:text-lg @[768px]:text-lg">
                Unlimited eligible treatments - £125/month
              </p>
              <p className="mt-1 text-xs text-white/80 lg:text-sm @[640px]:text-sm">
                Access model sessions as included or supplement-based member bookings.
              </p>
            </div>
            <span className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full bg-white/20 px-4 py-2 text-xs font-bold text-white backdrop-blur transition-colors group-hover:bg-white/30 sm:self-center @[480px]:self-center lg:text-sm @[640px]:text-sm">
              Learn more
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
              </svg>
            </span>
          </div>
        </Link>

        {/* Two main columns */}
        <div className="flex flex-col lg:flex-row @[768px]:flex-row gap-6 lg:gap-12 @[768px]:gap-12">
          {/* Model Sessions */}
          <div className="text-center lg:text-left @[768px]:text-left flex flex-col items-start justify-start flex-1">
            <Link href="/models/">
              <p className="block font-sans w-full font-bold mb-3 lg:mb-4 @[768px]:mb-4">
                <span className="mt-1 block text-xl lg:text-4xl @[640px]:text-3xl @[768px]:text-4xl xl:text-5xl @[1024px]:text-5xl">
                  <span className="text-gray-900">Model</span>
                  <span className="ml-2 text-brand-600">Sessions</span>
                  <span className="text-gray-900 block mt-1 lg:ml-2 lg:inline-block @[640px]:ml-2 @[640px]:inline-block text-xs lg:text-base @[640px]:text-sm @[768px]:text-base">
                    (Discounted Treatments)
                  </span>
                </span>
              </p>
            </Link>
            <p className="text-xs lg:text-base text-left @[640px]:text-sm @[768px]:text-base text-gray-600 mt-1">
              Book a model session with us to access heavily discounted beauty and
              aesthetics treatments. Treatments are carried out by students under
              expert tutor supervision, with sessions available 7 days a week.
              Prices from £5.
            </p>
            <div className="mt-4 lg:mt-5 @[768px]:mt-5 xl:mt-8 @[1024px]:mt-8 flex flex-col sm:flex-row lg:flex-row @[768px]:flex-row items-center justify-center lg:justify-start @[768px]:justify-start w-full lg:w-auto @[768px]:w-auto gap-3">
              <div className="w-full lg:w-auto @[768px]:w-auto rounded-md shadow">
                <Link
                  className="w-full lg:w-max @[768px]:w-max rounded-md bg-brand-600 px-4 lg:px-6 @[768px]:px-6 py-3 lg:py-4 @[768px]:py-4 text-xs lg:text-sm @[640px]:text-sm font-semibold text-white shadow-sm hover:bg-brand-500 outline outline-2 outline-offset-2 outline-brand-600 gradient-border transition-colors duration-200 hover:outline-brand-500 text-center block"
                  href="/models/"
                  onClick={() => {
                    trackMegaMenuLinkClick({
                      menu: menu.id,
                      linkText: "Book now - Model Sessions",
                      linkUrl: "/models/",
                    });
                  }}
                >
                  <span className="relative z-[1] text-center">Book now</span>
                </Link>
              </div>
              <Link
                className="w-full lg:w-max @[768px]:w-max rounded-md border border-brand-300 bg-white px-4 lg:px-6 @[768px]:px-6 py-3 lg:py-4 @[768px]:py-4 text-xs lg:text-sm @[640px]:text-sm font-semibold text-brand-700 shadow-sm hover:bg-brand-50 text-center block"
                href="/models/consultation/"
                onClick={() => {
                  trackMegaMenuLinkClick({
                    menu: menu.id,
                    linkText: "Book a consultation",
                    linkUrl: "/models/consultation/",
                  });
                }}
              >
                Book a consultation
              </Link>
            </div>
          </div>

          {/* Cosmetic Clinic */}
          <div className="text-center lg:text-left @[768px]:text-left flex flex-col items-start justify-start flex-1">
            <a
              href="https://cosmeticclinic.london/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackMegaMenuLinkClick({
                  menu: menu.id,
                  linkText: "Cosmetic Clinic",
                  linkUrl: "https://cosmeticclinic.london/",
                });
              }}
            >
              <p className="block font-sans w-full font-bold mb-3 lg:mb-4 @[768px]:mb-4">
                <span className="mt-1 block text-xl lg:text-4xl @[640px]:text-3xl @[768px]:text-4xl xl:text-5xl @[1024px]:text-5xl">
                  <span className="text-gray-900">Cosmetic</span>
                  <span className="ml-2 text-brand-600">Clinic</span>
                </span>
              </p>
            </a>
            <p className="text-xs lg:text-base text-left @[640px]:text-sm @[768px]:text-base text-gray-600 mt-1">
              Browse our extensive range of professional treatments at Cosmetic
              Clinic, including advanced laser hair removal suitable for all skin
              types, dermal fillers, and specialist skin and body treatments. All
              treatments are delivered by qualified practitioners using
              medical-grade technology.
            </p>
            <div className="mt-4 lg:mt-5 @[768px]:mt-5 xl:mt-8 @[1024px]:mt-8 flex flex-col lg:flex-row @[768px]:flex-row items-center justify-center lg:justify-start @[768px]:justify-start w-full lg:w-auto @[768px]:w-auto">
              <div className="w-full lg:w-auto @[768px]:w-auto rounded-md shadow">
                <a
                  target="_blank"
                  className="w-full lg:w-max @[768px]:w-max rounded-md bg-brand-600 px-4 lg:px-6 @[768px]:px-6 py-3 lg:py-4 @[768px]:py-4 text-xs lg:text-sm @[640px]:text-sm font-semibold text-white shadow-sm hover:bg-brand-500 outline outline-2 outline-offset-2 outline-brand-600 gradient-border transition-colors duration-200 hover:outline-brand-500 text-center block"
                  href="https://cosmeticclinic.london/"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackMegaMenuLinkClick({
                      menu: menu.id,
                      linkText: "Book now - Cosmetic Clinic",
                      linkUrl: "https://cosmeticclinic.london/",
                    });
                  }}
                >
                  <span className="relative z-[1] text-center">Book now</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
